import { useEffect, useState } from "react";
import { Phone, Sms, Email, SupportAgent } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { Avatar, Box, Button, CircularProgress, Stack, Typography, Divider } from "@mui/material";
import { webFetch } from "utilities/webFetch";
import config from "utilities/config";

interface BadgeData {
  firstName: string;
  lastName: string;
  title: string;
  inspectorNumber: string;
  inspectorPhone?: string;
  customerServicePhone: string;
  customerServiceEmail: string;
}

const mockBadgeData: BadgeData = {
  firstName: "Jane",
  lastName: "S",
  title: "Elite Field Representative",
  inspectorNumber: "112358",
  inspectorPhone: "+15551234567",
  customerServicePhone: "+18008758339",
  customerServiceEmail: "CustomerService@MuellerReports.com",
};

const logoSx = {
  width: 50,
  height: 50,
  backgroundColor: "var(--mueller-red)",
  color: "white",
  fontSize: 32,
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const photoSx = {
  my: 1,
  width: 160,
  height: 200,
  backgroundColor: "#e0e0e0",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  "& img": { width: "100%", height: "100%", objectFit: "cover" },
};

const avatarSx = {
  width: 120,
  height: 120,
  fontSize: 48,
  backgroundColor: "var(--mueller-red)",
};

const buttonSx = {
  height: 55,
  borderRadius: "15px",
  fontSize: 20,
  textTransform: "none",
  backgroundColor: "var(--mueller-red)",
  "&:hover": { backgroundColor: "#a00d2e" },
  justifyContent: "center",
  "& .MuiButton-startIcon": {
    position: "absolute",
    left: 20,
    "& svg": { fontSize: 32 },
  },
};

const VirtualBadge = () => {
  const params = useParams();

  const [data, setData] = useState<BadgeData>(null);
  const [photoUrl, setPhotoUrl] = useState<string>(null);
  const [photoError, setPhotoError] = useState<boolean>(false);

  useEffect(() => {
    const key = params?.workItemId;
    if (!key) return;

    if (key === "0") {
      setData(mockBadgeData);
      setPhotoError(true);
      return;
    }

    webFetch({
      url: `/inspector-badge/photo-exists/${key}`,
      method: "GET",
      onSuccess: (exists: boolean) => {
        if (exists) {
          setPhotoUrl(`${config.webApiRoot}/inspector-badge/photo/${key}`);
        } else {
          setPhotoError(true);
        }
      },
      onFailure: () => setPhotoError(true),
    });

    webFetch({
      url: `/inspector-badge/details/${key}`,
      method: "GET",
      onSuccess: (result: BadgeData) => setData(result),
    });
  }, [params]);


  if (!data) return <Stack alignItems="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "var(--mueller-red)" }} /></Stack>;

  const initials = `${data.firstName?.[0] ?? ""}${data.lastName?.[0] ?? ""}`;

  return (
    <Stack alignItems="center" spacing={1} sx={{ maxWidth: 480, margin: "var(--padding) auto", padding: "var(--padding)" }}>
      <Stack direction="row" alignItems="center" spacing={1} width="100%">
        <Box sx={logoSx}>M</Box>
        <Typography variant="h5" sx={{ color: "var(--mueller-red)" }}>Mueller Reports</Typography>
      </Stack>

      {photoError ? (
        <Avatar sx={avatarSx}>{initials}</Avatar>
      ) : (
        <Box sx={photoSx}>
          {photoUrl
            ? <img src={photoUrl} alt={`${data.firstName} ${data.lastName}`} />
            : <Typography color="textSecondary">Photo</Typography>
          }
        </Box>
      )}

      <Stack alignItems="center" width="100%">
        <Typography variant="h5">{data.firstName} {data.lastName}</Typography>
        <Divider sx={{ width: "100%", my: 1 }} />
        <Typography variant="body1" fontSize={18}>{data.title}</Typography>
        <Typography variant="body1" fontSize={18} fontWeight="bold">ID: {data.inspectorNumber}</Typography>
      </Stack>

      <Stack width="100%" spacing={1}>
        {data.inspectorPhone && (
          <>
            <Button variant="contained" startIcon={<Phone />} onClick={() => window.location.href = `tel:${data.inspectorPhone}`} sx={buttonSx}>
              Call Field Rep
            </Button>
            <Button variant="contained" startIcon={<Sms />} onClick={() => window.location.href = `sms:${data.inspectorPhone}`} sx={buttonSx}>
              Text Field Rep
            </Button>
          </>
        )}

        <Box>
            <Typography textAlign="center" fontSize={20} sx={{mt:1}}>Mueller Customer Service</Typography>
            <Divider sx={{ my: 1 }} />
        </Box>
        
        <Button variant="contained" startIcon={<SupportAgent />} onClick={() => window.location.href = `tel:${data.customerServicePhone}`} sx={buttonSx}>
          Call Customer Service
        </Button>
        <Button variant="contained" startIcon={<Email />} onClick={() => window.location.href = `mailto:${data.customerServiceEmail}`} sx={buttonSx}>
          Email Customer Service
        </Button>
      </Stack>
    </Stack>
  );
};

export default VirtualBadge;